import * as React from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useAppConfigsApi } from "../../api";
import type { AudioPrice, CatalogModel, LlmCatalog, LlmModelCaps, LlmProvider, ReasoningEffort } from "../../models/domain";
import { errorMessage } from "../ui";
import {
  CAP_LABELS,
  CHAT_CAPS,
  type ChatCap,
  DEFAULT_CAPS,
  type ModelKind,
  REASONING_EFFORTS,
  modelKey,
  modelKind,
  parseRoutingOrder,
  routingOrderProblem,
} from "./llmCatalog";

export interface DeclareTarget {
  provider: string;
  model: string;
}

interface Props {
  /** Ouvert sur ce couple (pré-rempli) ; null = fermé. */
  target: DeclareTarget | null;
  providers: Record<string, LlmProvider>;
  /** Modèles déjà déclarés, avec leur tarif : redéclarer part de leurs valeurs. */
  models?: Record<string, CatalogModel>;
  onClose: () => void;
  /** Le catalogue rendu par le serveur après la déclaration. */
  onDeclared: (catalog: LlmCatalog) => void;
}

/**
 * Les tarifs tels que le formulaire les saisit. Un modèle audio se paie aux
 * jetons (`audioIn`, `audioOut`) ou à l'unité de son type (`perUnit` : la
 * minute d'audio pour la transcription, le million de caractères pour la
 * synthèse), selon `audioBy`. `textOut` : le prix facultatif des jetons de
 * texte d'un modèle de synthèse temps réel, au jeton seulement.
 */
type PriceForm = {
  input: string;
  cached: string;
  output: string;
  cacheWrite: string;
  perKPages: string;
  audioBy: "tokens" | "unit";
  audioIn: string;
  audioOut: string;
  perUnit: string;
  textOut: string;
};

const EMPTY_PRICE: PriceForm = {
  input: "",
  cached: "",
  output: "",
  cacheWrite: "",
  perKPages: "",
  audioBy: "tokens",
  audioIn: "",
  audioOut: "",
  perUnit: "",
  textOut: "",
};

/** Le type de modèle (la route de la passerelle qui l'appelle), et ce qu'il sert. */
const KINDS: { kind: ModelKind; label: string; hint: string }[] = [
  { kind: "chat", label: "Chat", hint: "Le cas courant : objectifs, faits, quêtes, compagnon, OCR par images." },
  {
    kind: "ocr_endpoint",
    label: "OCR dédié (ex. Mistral OCR)",
    hint: "Appelé par la route OCR de la passerelle, jamais en chat : il ne sert que la lecture des pages scannées, et se paie à la page.",
  },
  {
    kind: "tts",
    label: "Synthèse vocale (tts)",
    hint: "Appelé par la route de synthèse de la passerelle, jamais en chat : il ne sert que la voix du compagnon (lecture à voix haute).",
  },
  {
    kind: "stt",
    label: "Transcription (stt)",
    hint: "Appelé par la route de transcription de la passerelle, jamais en chat : il ne sert que l'écoute du compagnon (dictée).",
  },
];

/** Le tarif « à l'unité » d'un modèle audio : ce que le fournisseur publie quand il ne compte pas en jetons. */
const AUDIO_UNIT: Record<"tts" | "stt", { radio: string; field: string; key: "per_1m_characters" | "per_audio_minute" }> = {
  tts: { radio: "Au million de caractères lus", field: "Prix pour 1 M caractères ($)", key: "per_1m_characters" },
  stt: { radio: "À la minute d'audio", field: "Prix par minute ($)", key: "per_audio_minute" },
};

/** Les libellés des jetons d'un modèle audio : ce qui entre et ce qui sort n'est pas la même matière. */
const AUDIO_TOKENS: Record<"tts" | "stt", [string, string]> = {
  tts: ["Entrée (texte)", "Sortie (audio)"],
  stt: ["Entrée (audio)", "Sortie (texte)"],
};

/**
 * Le routage d'agrégateur tel que le formulaire le saisit. Par défaut, ce qui
 * rend une mesure reproductible : un seul hébergeur, pas de repli, tous les
 * paramètres exigés, pas de conservation des données.
 */
type RoutingForm = { on: boolean; order: string; allowFallbacks: boolean; requireParameters: boolean; dataCollection: "" | "allow" | "deny" };

const NO_ROUTING: RoutingForm = { on: false, order: "", allowFallbacks: false, requireParameters: true, dataCollection: "deny" };

const CAP_HINTS: Record<ChatCap, string> = {
  strict_schema: "response_format json_schema strict (objectifs, génération des quêtes)",
  json_mode: "response_format json_object (faits, relecture des quêtes)",
  streaming: "réponse en flux (génération des quêtes, compagnon)",
  temperature: "accepte une température (les modèles à raisonnement la refusent souvent)",
  reasoning: "modèle à raisonnement : effort réglable, jetons de réflexion facturés en sortie",
  vision: "lit les images (OCR)",
};

const num = (v: string) => (v.trim() === "" ? NaN : Number(v.replace(",", ".")));

/**
 * Un modèle d'une route dédiée (OCR, synthèse, transcription) n'a aucune
 * capacité de chat : on les éteint toutes, pour que la liste et les règles par
 * rôle ne lui en prêtent pas, et on ne pose que le drapeau de sa route (le
 * serveur refuse qu'elles se cumulent). La sortie maximale d'un OCR dédié est
 * gardée telle quelle ; celle d'un modèle audio vaut 0 (il ne rend pas de
 * jetons de chat, le serveur l'admet).
 */
const dedicatedCaps = (c: LlmModelCaps, kind: Exclude<ModelKind, "chat">): LlmModelCaps => ({
  ...c,
  strict_schema: false,
  json_mode: false,
  streaming: false,
  temperature: false,
  reasoning: false,
  vision: false,
  default_effort: undefined,
  reasoning_headroom: 0,
  max_output: kind === "ocr_endpoint" ? c.max_output : 0,
  ocr_endpoint: kind === "ocr_endpoint" || undefined,
  tts: kind === "tts" || undefined,
  // Le temps réel n'existe que pour la synthèse : changer de type l'éteint.
  realtime: (kind === "tts" && c.realtime) || undefined,
  stt: kind === "stt" || undefined,
  max_tokens_only: undefined,
  routing: undefined,
  response_healing: undefined,
});

/**
 * « Déclarer un modèle » : fournisseur, nom libre, tarifs OBLIGATOIRES
 * (insérés dans ai_model_price, `source = saisie_admin`) et capacités. Sans
 * tarif, un modèle ne pourrait servir aucun rôle : son coût ne serait compté
 * nulle part. Un modèle d'OCR dédié se paie à la page (api_kind `ocr`), un
 * modèle de synthèse ou de transcription selon son tarif audio (api_kind
 * `tts`/`stt`), un modèle de chat aux jetons.
 */
export const DeclareModelDialog: React.FC<Props> = ({ target, providers, models, onClose, onDeclared }) => {
  const { declareLlmModel } = useAppConfigsApi();
  const [provider, setProvider] = React.useState("");
  const [model, setModel] = React.useState("");
  const [caps, setCaps] = React.useState<LlmModelCaps>(DEFAULT_CAPS);
  const [price, setPrice] = React.useState<PriceForm>(EMPTY_PRICE);
  const [routing, setRouting] = React.useState<RoutingForm>(NO_ROUTING);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  // Ouverture : on part du modèle déjà déclaré s'il existe, sinon des
  // capacités d'un modèle de chat courant.
  React.useEffect(() => {
    if (!target) return;
    const existing = models?.[modelKey(target.provider, target.model.trim())];
    setProvider(target.provider);
    setModel(target.model.trim());
    if (existing) {
      const { provider: _p, model: _m, price: p, ocr_price: o, audio_price: a, ...c } = existing;
      setCaps(c);
      const unitKey = c.tts ? AUDIO_UNIT.tts.key : AUDIO_UNIT.stt.key;
      const perUnit = a?.[unitKey];
      setPrice({
        input: p ? String(p.input_per_1m) : "",
        cached: p ? String(p.cached_input_per_1m) : "",
        output: p ? String(p.output_per_1m) : "",
        cacheWrite: p?.cache_write_per_1m != null ? String(p.cache_write_per_1m) : "",
        perKPages: o ? String(o.per_1k_pages) : "",
        audioBy: perUnit ? "unit" : "tokens",
        audioIn: a?.input_per_1m ? String(a.input_per_1m) : "",
        audioOut: a?.output_per_1m ? String(a.output_per_1m) : "",
        perUnit: perUnit ? String(perUnit) : "",
        textOut: a?.text_output_per_1m ? String(a.text_output_per_1m) : "",
      });
      setRouting(
        c.routing
          ? {
              on: true,
              order: c.routing.order.join(", "),
              allowFallbacks: c.routing.allow_fallbacks,
              requireParameters: c.routing.require_parameters,
              dataCollection: c.routing.data_collection ?? "",
            }
          : NO_ROUTING,
      );
    } else {
      setCaps(DEFAULT_CAPS);
      setPrice(EMPTY_PRICE);
      setRouting(NO_ROUTING);
    }
    setError(null);
  }, [target, models]);

  const kind = modelKind(caps);
  const ocrEndpoint = kind === "ocr_endpoint";
  const audio = kind === "tts" || kind === "stt" ? kind : null;
  const chat = kind === "chat";
  // Le routage d'agrégateur ne part que vers un fournisseur compatible : OpenAI
  // et Anthropic refuseraient le champ (le serveur et la passerelle aussi).
  const compatible = providers[provider]?.kind === "openai_compatible";
  const routed = routing.on && compatible && chat;
  // Anthropic n'a pas d'API audio : le serveur et la passerelle refusent un
  // modèle tts/stt chez lui.
  const audioRefused = Boolean(audio) && providers[provider]?.kind === "anthropic";
  // L'API Realtime est un protocole propre à OpenAI : le serveur refuse le
  // temps réel chez un fournisseur d'un autre type, même compatible OpenAI.
  const realtimeAllowed = providers[provider]?.kind === "openai";
  const realtimeRefused = audio === "tts" && Boolean(caps.realtime) && !realtimeAllowed;
  const routingOrder = parseRoutingOrder(routing.order);
  const routingProblem = routed ? routingOrderProblem(routingOrder) : null;
  const prices = {
    input: num(price.input),
    cached: num(price.cached),
    output: num(price.output),
    cacheWrite: num(price.cacheWrite),
    perKPages: num(price.perKPages),
    audioIn: num(price.audioIn),
    audioOut: num(price.audioOut),
    perUnit: num(price.perUnit),
    textOut: num(price.textOut),
  };
  // Écriture du cache : vide = facturée au prix d'entrée (OpenAI ne la
  // distingue pas) ; posée, elle doit être un prix positif.
  const cacheWriteOk = price.cacheWrite.trim() === "" || prices.cacheWrite > 0;
  // Sortie texte : vide = au prix de sortie ; posée, un prix positif.
  const textOutOk = price.textOut.trim() === "" || prices.textOut > 0;
  const textOutShown = audio === "tts" && price.audioBy === "tokens";
  const priceOk = audio
    ? price.audioBy === "unit"
      ? prices.perUnit > 0
      : prices.audioIn > 0 && prices.audioOut > 0 && (!textOutShown || textOutOk)
    : ocrEndpoint
      ? prices.perKPages > 0
      : prices.input > 0 && prices.output > 0 && prices.cached >= 0 && cacheWriteOk;
  const valid =
    Boolean(provider) &&
    model.trim() !== "" &&
    !/\s/.test(model.trim()) &&
    priceOk &&
    (audio ? !audioRefused && !realtimeRefused : caps.max_output > 0) &&
    !routingProblem;

  // Revenir au chat repart des capacités d'un modèle courant (celles d'une
  // route dédiée sont toutes éteintes) ; sa sortie maximale aussi si l'audio
  // l'avait mise à 0.
  const changeKind = (k: ModelKind) =>
    setCaps((c) =>
      k === "chat" ? { ...DEFAULT_CAPS, max_output: c.max_output > 0 ? c.max_output : DEFAULT_CAPS.max_output } : dedicatedCaps(c, k),
    );

  /** Le tarif audio envoyé : jetons OU unité, jamais les deux (le serveur refuse le champ de l'autre type). */
  const audioPrice = (a: "tts" | "stt"): AudioPrice =>
    price.audioBy === "unit"
      ? { [AUDIO_UNIT[a].key]: prices.perUnit }
      : {
          input_per_1m: prices.audioIn,
          output_per_1m: prices.audioOut,
          ...(a === "tts" && price.textOut.trim() !== "" ? { text_output_per_1m: prices.textOut } : {}),
        };

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      const base: LlmModelCaps = caps.reasoning ? caps : { ...caps, default_effort: undefined, reasoning_headroom: 0 };
      const capabilities: LlmModelCaps = {
        ...base,
        max_tokens_only: base.max_tokens_only || undefined,
        response_healing: compatible && base.response_healing ? true : undefined,
        routing: routed
          ? {
              order: routingOrder,
              allow_fallbacks: routing.allowFallbacks,
              require_parameters: routing.requireParameters,
              ...(routing.dataCollection ? { data_collection: routing.dataCollection } : {}),
            }
          : undefined,
      };
      const { data } = await declareLlmModel(
        audio
          ? { provider, model: model.trim(), capabilities: dedicatedCaps(caps, audio), audio_price: audioPrice(audio) }
          : ocrEndpoint
            ? { provider, model: model.trim(), capabilities, ocr_price: { per_1k_pages: prices.perKPages } }
            : {
                provider,
                model: model.trim(),
                capabilities: { ...capabilities, ocr_endpoint: undefined },
                price: {
                  input_per_1m: prices.input,
                  cached_input_per_1m: prices.cached,
                  output_per_1m: prices.output,
                  ...(price.cacheWrite.trim() === "" ? {} : { cache_write_per_1m: prices.cacheWrite }),
                },
              },
      );
      onDeclared(data);
      onClose();
    } catch (e) {
      setError(errorMessage(e, "Déclaration refusée."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={Boolean(target)} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Déclarer un modèle</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            <TextField
              select
              label="Fournisseur"
              value={provider}
              onChange={(e) => setProvider(e.target.value)}
              sx={{ minWidth: 200 }}
            >
              {Object.entries(providers).map(([id, p]) => (
                <MenuItem key={id} value={id}>
                  {p.label} ({id}){p.enabled ? "" : " — désactivé"}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Modèle"
              value={model}
              onChange={(e) => setModel(e.target.value)}
              fullWidth
              helperText="Le nom exact attendu par le fournisseur, ex. gpt-5-mini."
            />
          </Stack>

          <TextField
            select
            label="Type de modèle"
            value={kind}
            onChange={(e) => changeKind(e.target.value as ModelKind)}
            error={audioRefused}
            helperText={
              audioRefused
                ? "Anthropic n'a pas d'API audio : choisissez un fournisseur OpenAI ou compatible OpenAI."
                : KINDS.find((k) => k.kind === kind)?.hint
            }
            sx={{ maxWidth: 360 }}
          >
            {KINDS.map((k) => (
              <MenuItem key={k.kind} value={k.kind}>
                {k.label}
              </MenuItem>
            ))}
          </TextField>

          {audio === "tts" && (
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={Boolean(caps.realtime)}
                  disabled={!caps.realtime && !realtimeAllowed}
                  onChange={(e) => setCaps((x) => ({ ...x, realtime: e.target.checked || undefined }))}
                />
              }
              label={
                <Typography variant="body2" color={realtimeRefused ? "error" : undefined}>
                  Temps réel (API Realtime, WebSocket){" "}
                  <Typography component="span" variant="caption" color={realtimeRefused ? "error" : "text.secondary"}>
                    —{" "}
                    {realtimeRefused || !realtimeAllowed
                      ? "réservé aux fournisseurs de type OpenAI (protocole propre à OpenAI)."
                      : "modèle servi seulement par l'API Realtime (ex. gpt-realtime-2.1-mini) ; la passerelle le relaie en mp3 ou wav."}
                  </Typography>
                </Typography>
              }
            />
          )}

          {audio ? (
            <Box>
              <Typography variant="subtitle2">Tarif</Typography>
              <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>
                Obligatoire : enregistré dans les tarifs (saisie administrateur), en vigueur tout de suite. Celui que
                publie le fournisseur : aux jetons, ou {audio === "tts" ? "au million de caractères" : "à la minute"}.
              </Typography>
              <RadioGroup
                row
                value={price.audioBy}
                onChange={(e) => setPrice((p) => ({ ...p, audioBy: e.target.value as PriceForm["audioBy"] }))}
              >
                <FormControlLabel
                  value="tokens"
                  control={<Radio size="small" />}
                  label={<Typography variant="body2">Aux jetons ($ / M)</Typography>}
                />
                <FormControlLabel
                  value="unit"
                  control={<Radio size="small" />}
                  label={<Typography variant="body2">{AUDIO_UNIT[audio].radio}</Typography>}
                />
              </RadioGroup>
              {price.audioBy === "tokens" ? (
                <>
                  <Stack direction="row" spacing={1.5} sx={{ mt: 1 }}>
                    {(
                      [
                        ["audioIn", AUDIO_TOKENS[audio][0]],
                        ["audioOut", AUDIO_TOKENS[audio][1]],
                      ] as const
                    ).map(([k, label]) => (
                      <TextField
                        key={k}
                        label={label}
                        value={price[k]}
                        onChange={(e) => setPrice((p) => ({ ...p, [k]: e.target.value }))}
                        inputProps={{ inputMode: "decimal" }}
                        error={price[k] !== "" && !(prices[k] > 0)}
                        size="small"
                      />
                    ))}
                  </Stack>
                  {textOutShown && (
                    <TextField
                      label="Sortie texte ($/M jetons)"
                      value={price.textOut}
                      onChange={(e) => setPrice((p) => ({ ...p, textOut: e.target.value }))}
                      inputProps={{ inputMode: "decimal" }}
                      error={!textOutOk}
                      helperText="Facultatif : transcription et raisonnement du modèle temps réel, payés moins cher que l'audio. Vide = au prix de sortie."
                      size="small"
                      sx={{ mt: 1.5 }}
                    />
                  )}
                </>
              ) : (
                <TextField
                  label={AUDIO_UNIT[audio].field}
                  value={price.perUnit}
                  onChange={(e) => setPrice((p) => ({ ...p, perUnit: e.target.value }))}
                  inputProps={{ inputMode: "decimal" }}
                  error={price.perUnit !== "" && !(prices.perUnit > 0)}
                  size="small"
                  sx={{ mt: 1 }}
                />
              )}
            </Box>
          ) : ocrEndpoint ? (
            <Box>
              <Typography variant="subtitle2">Tarif (dollars pour 1 000 pages)</Typography>
              <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>
                Obligatoire : enregistré dans les tarifs (saisie administrateur), en vigueur tout de suite.
              </Typography>
              <TextField
                label="Prix pour 1 000 pages ($)"
                value={price.perKPages}
                onChange={(e) => setPrice((p) => ({ ...p, perKPages: e.target.value }))}
                inputProps={{ inputMode: "decimal" }}
                error={price.perKPages !== "" && !(prices.perKPages > 0)}
                size="small"
              />
            </Box>
          ) : (
            <Box>
              <Typography variant="subtitle2">Tarif (dollars par million de jetons)</Typography>
              <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>
                Obligatoire : enregistré dans les tarifs (saisie administrateur), en vigueur tout de suite. Un tarif
                identique au tarif en vigueur n'est pas réenregistré.
              </Typography>
              <Stack direction="row" spacing={1.5}>
                {(
                  [
                    ["input", "Entrée"],
                    ["cached", "Entrée en cache"],
                    ["output", "Sortie"],
                  ] as const
                ).map(([k, label]) => (
                  <TextField
                    key={k}
                    label={label}
                    value={price[k]}
                    onChange={(e) => setPrice((p) => ({ ...p, [k]: e.target.value }))}
                    inputProps={{ inputMode: "decimal" }}
                    error={price[k] !== "" && !(k === "cached" ? prices[k] >= 0 : prices[k] > 0)}
                    size="small"
                  />
                ))}
              </Stack>
              <TextField
                label="Écriture du cache ($/M jetons)"
                value={price.cacheWrite}
                onChange={(e) => setPrice((p) => ({ ...p, cacheWrite: e.target.value }))}
                inputProps={{ inputMode: "decimal" }}
                error={!cacheWriteOk}
                helperText="Facultatif. Anthropic : 1,25 × l'entrée. Vide = facturée au prix d'entrée (cas d'OpenAI)."
                size="small"
                sx={{ mt: 1.5 }}
              />
            </Box>
          )}

          {!audio && (
            <Box>
              <Typography variant="subtitle2">Capacités</Typography>
              <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 0.5 }}>
                Ce que le modèle accepte vraiment : chaque rôle n'admet que les modèles qui font ce que son service demande.
              </Typography>
              <Stack>
                {chat &&
                  CHAT_CAPS.map((c) => (
                  <FormControlLabel
                    key={c}
                    control={
                      <Checkbox
                        size="small"
                        checked={Boolean(caps[c])}
                        onChange={(e) => setCaps((x) => ({ ...x, [c]: e.target.checked }))}
                      />
                    }
                    label={
                      <Typography variant="body2">
                        {CAP_LABELS[c]}{" "}
                        <Typography component="span" variant="caption" color="text.secondary">
                          — {CAP_HINTS[c]}
                        </Typography>
                      </Typography>
                    }
                  />
                ))}
              </Stack>
              <Stack direction="row" spacing={1.5} sx={{ mt: 1.5 }}>
                <TextField
                  label="Sortie maximale (jetons)"
                  type="number"
                  size="small"
                  value={caps.max_output}
                  onChange={(e) => setCaps((x) => ({ ...x, max_output: Number(e.target.value) }))}
                  error={!(caps.max_output > 0)}
                />
                {caps.reasoning && (
                  <>
                    <TextField
                      select
                      label="Effort par défaut"
                      size="small"
                      value={caps.default_effort ?? ""}
                      onChange={(e) =>
                        setCaps((x) => ({ ...x, default_effort: (e.target.value || undefined) as ReasoningEffort | undefined }))
                      }
                      sx={{ minWidth: 150 }}
                    >
                      <MenuItem value="">celui du fournisseur</MenuItem>
                      {REASONING_EFFORTS.map((e) => (
                        <MenuItem key={e} value={e}>
                          {e}
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      label="Marge de raisonnement (jetons)"
                      type="number"
                      size="small"
                      value={caps.reasoning_headroom}
                      onChange={(e) => setCaps((x) => ({ ...x, reasoning_headroom: Math.max(0, Number(e.target.value)) }))}
                      helperText="Ajoutée à la borne de sortie de chaque appel."
                    />
                  </>
                )}
              </Stack>
            </Box>
          )}
          {chat && (
            <Box>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={Boolean(caps.max_tokens_only)}
                    onChange={(e) => setCaps((x) => ({ ...x, max_tokens_only: e.target.checked }))}
                  />
                }
                label={
                  <Typography variant="body2">
                    max_tokens seulement{" "}
                    <Typography component="span" variant="caption" color="text.secondary">
                      — le fournisseur ne connaît pas max_completion_tokens (OpenRouter) : la borne et la marge de
                      raisonnement partent dans max_tokens.
                    </Typography>
                  </Typography>
                }
              />
            </Box>
          )}

          {chat && compatible && (
            <Box>
              <Typography variant="subtitle2">Routage (agrégateur)</Typography>
              <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 0.5 }}>
                Chez un agrégateur comme OpenRouter, épingler l'hébergeur : sinon il change à chaque appel (modèles
                ouverts parfois quantifiés, JSON strict ignoré par certains), et on mesure un mélange.
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox size="small" checked={routing.on} onChange={(e) => setRouting((r) => ({ ...r, on: e.target.checked }))} />
                }
                label={<Typography variant="body2">Épingler l'hébergeur</Typography>}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={Boolean(caps.response_healing)}
                    onChange={(e) => setCaps((x) => ({ ...x, response_healing: e.target.checked }))}
                  />
                }
                label={
                  <Typography variant="body2">
                    Réparer le JSON{" "}
                    <Typography component="span" variant="caption" color="text.secondary">
                      — l'agrégateur extrait le JSON des réponses non diffusées (Claude par OpenRouter l'entoure de
                      ```json en mode JSON)
                    </Typography>
                  </Typography>
                }
              />
              {routing.on && (
                <Stack spacing={1} sx={{ mt: 0.5 }}>
                  <TextField
                    label="Hébergeurs, dans l'ordre"
                    size="small"
                    value={routing.order}
                    onChange={(e) => setRouting((r) => ({ ...r, order: e.target.value }))}
                    error={Boolean(routingProblem)}
                    helperText={routingProblem ?? "Slugs d'OpenRouter, ex. anthropic, google-ai-studio, mistral/zdr, cerebras/fp16."}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={routing.allowFallbacks}
                        onChange={(e) => setRouting((r) => ({ ...r, allowFallbacks: e.target.checked }))}
                      />
                    }
                    label={<Typography variant="body2">Repli autorisé vers un autre hébergeur</Typography>}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={routing.requireParameters}
                        onChange={(e) => setRouting((r) => ({ ...r, requireParameters: e.target.checked }))}
                      />
                    }
                    label={
                      <Typography variant="body2">
                        Paramètres exigés{" "}
                        <Typography component="span" variant="caption" color="text.secondary">
                          — seulement un hébergeur qui accepte tous les paramètres (JSON strict compris)
                        </Typography>
                      </Typography>
                    }
                  />
                  <TextField
                    select
                    label="Conservation des données"
                    size="small"
                    value={routing.dataCollection}
                    onChange={(e) => setRouting((r) => ({ ...r, dataCollection: e.target.value as RoutingForm["dataCollection"] }))}
                    sx={{ maxWidth: 320 }}
                  >
                    <MenuItem value="deny">refusée (deny)</MenuItem>
                    <MenuItem value="allow">admise (allow)</MenuItem>
                    <MenuItem value="">réglage du compte</MenuItem>
                  </TextField>
                </Stack>
              )}
            </Box>
          )}
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Annuler</Button>
        <Button variant="contained" disabled={!valid || saving} onClick={() => void submit()}>
          {saving ? "Déclaration…" : "Déclarer"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeclareModelDialog;
